import React from 'react';
import ReactQuill from 'react-quill';
import { FieldContext } from '../../../types/enums/FieldContext';

const Embed = ReactQuill.Quill.import('blots/embed');

export interface FieldTagBlotConfig {
  backgroundColor: string;
  textColor: string;
  supportsPriorValue?: boolean;
}

export interface SlackMessageStruct {
  variables?: Array<{
    fieldIds: string[];
    fieldContext?: FieldContext;
  }>;
}

/**
 * Factory function to create FieldTagBlot class with dependencies and configuration
 * This consolidates the duplicate FieldTagBlot implementations across components
 */
export const createFieldTagBlot = (
  quillRef: React.RefObject<ReactQuill>,
  actionMessage: SlackMessageStruct | undefined,
  getLabelFromIdsWithObjectType: (fieldIds: string[]) => string[],
  config: FieldTagBlotConfig
) => {
  class FieldTagBlot extends Embed {
    static create(createData: any) {
      const node = super.create();
      node.setAttribute('chipId', createData.fieldNum);
      const innerDiv = document.createElement('span');
      innerDiv.setAttribute('contenteditable', 'false');
      innerDiv.classList.add('disablePointerEvents');
      
      const taglineCss = `
        text-transform: capitalize;
        border-radius: 4px;
        background: ${config.backgroundColor};
        padding: 0 4px;
        font-size: 12px;
        color: ${config.textColor};
        display: inline-block;
      `;
      innerDiv.setAttribute('style', taglineCss);

      let textLabel: string = createData.fieldTitle;
      if (!textLabel) {
        const fieldStruct = actionMessage?.variables?.[createData.fieldNum];
        if (fieldStruct) {
          const fieldLabels = getLabelFromIdsWithObjectType(fieldStruct.fieldIds).join('.');
          
          if (config.supportsPriorValue && fieldStruct.fieldContext === FieldContext.PRIOR_VALUE) {
            textLabel = `Prior value of ${fieldLabels}`;
          } else {
            textLabel = fieldLabels;
          }
        } else {
          textLabel = '';
        }
      }

      const exitNode = document.createElement('span');
      exitNode.setAttribute('contenteditable', 'false');
      exitNode.classList.add('tagDeleteClass');
      const deleteTagCss = `
        font-size: 10px;
        display: inline-block;
        padding: 0 6px;
        cursor: pointer;
        position: relative;
      `;
      exitNode.setAttribute('style', deleteTagCss);
      exitNode.innerText = 'X';

      exitNode.addEventListener('click', function () {
        if (quillRef.current) {
          const quillEditor = quillRef.current.getEditor();
          const range = quillEditor.getSelection(true);
          quillEditor.removeFormat(range.index, 1, 'user');
        }
      });

      innerDiv.innerText = textLabel;
      innerDiv.append(exitNode);
      node.appendChild(innerDiv);
      return node;
    }

    static formats(node: any) {
      const _num = node.getAttribute('chipId');
      return {
        fieldNum: _num,
      };
    }

    static value(node: any) {
      const _num = node.getAttribute('chipId');
      return {
        fieldNum: _num,
      };
    }
  }

  FieldTagBlot.blotName = 'sfField';
  FieldTagBlot.tagName = 'span';
  FieldTagBlot.className = 'sfField';
  
  return FieldTagBlot;
};
